import type { ComponentType } from 'react';
import {
  IconCamera,
  IconCircleSlash,
  IconList,
  IconListChecks,
  IconMessageSquare,
  IconSlidersHorizontal,
  IconToggleLeft,
  IconTriangleAlert,
  IconType,
  type IconProps,
} from '../icons';
import type { AnswerType, CriticalScope, NotApplicableScope, RequirementTrigger } from '../api/types';
import { hasCode, tCode } from '../i18n';
import { useLanguage } from '../i18n/react';
import { Badge, type BadgeTone } from './Badge';

/**
 * What a server code looks like on screen.
 *
 * <p>The design package shipped placeholder codes taken from a screenshot (`SLIDER_1_5`,
 * `YES_NO`); these are the real wire values, the same names the database stores and the form
 * snapshot writes.</p>
 *
 * <p>A code with no entry here still renders — neutral badge, the code itself as the label.
 * That is deliberate and it is for the field app: a device syncs with whatever server it
 * reaches, which may be newer than the build it is running, and an answer type added last
 * week must not blank out a question an auditor is standing in front of.</p>
 */
export interface CodeVisual {
  tone: BadgeTone;
  Icon?: ComponentType<IconProps>;
}

const NEUTRAL: CodeVisual = { tone: 'neutral' };

const ANSWER_TYPE_VISUALS: Readonly<Record<string, CodeVisual>> = {
  YesNo: { tone: 'boolean', Icon: IconToggleLeft },
  Scale: { tone: 'scale', Icon: IconSlidersHorizontal },
  SingleChoice: { tone: 'scale', Icon: IconList },
  MultipleChoice: { tone: 'scale', Icon: IconListChecks },
  Text: { tone: 'neutral', Icon: IconType },
};

export const answerTypeVisual = (code: string): CodeVisual => ANSWER_TYPE_VISUALS[code] ?? NEUTRAL;

/** The question type, as a square badge. */
export function AnswerTypeBadge({ type }: { type: AnswerType | string }) {
  useLanguage();
  const { tone, Icon } = answerTypeVisual(type);

  return (
    <Badge tone={tone} icon={Icon} shape="square">
      {tCode('answerType', type)}
    </Badge>
  );
}

/**
 * The behaviours of a question, as pills, in a fixed order.
 *
 * <p>Critical first, always, and in red. It is the one that changes what the whole visit
 * scores, so it is the one that must not be the third thing the eye finds.</p>
 */
export interface Behaviours {
  criticalScope: CriticalScope;
  allowsNotApplicable: boolean;
  notApplicableScope: NotApplicableScope;
  commentRequirement: RequirementTrigger;
  photoRequirement: RequirementTrigger;
}

interface BehaviourBadge {
  /** `namespace.CODE`, so an unknown code falls back to the code. */
  namespace: string;
  code: string;
  tone: BadgeTone;
  Icon: ComponentType<IconProps>;
}

/** The order is the ranking: critical, then skippable, then what is owed. */
export function behavioursOf(q: Behaviours): BehaviourBadge[] {
  const badges: BehaviourBadge[] = [];

  if (q.criticalScope !== 'None') {
    badges.push({ namespace: 'badgeCritical', code: q.criticalScope, tone: 'critical', Icon: IconTriangleAlert });
  }

  if (q.allowsNotApplicable) {
    // One rule now (ADR-022), so one badge; a library row written before it may still
    // carry form scope, and that no longer means anything.
    badges.push({ namespace: 'badgeNa', code: 'Category', tone: 'na', Icon: IconCircleSlash });
  }

  if (q.commentRequirement !== 'Never') {
    badges.push({ namespace: 'badgeComment', code: q.commentRequirement, tone: 'comment', Icon: IconMessageSquare });
  }

  if (q.photoRequirement !== 'Never') {
    badges.push({ namespace: 'badgePhoto', code: q.photoRequirement, tone: 'photo', Icon: IconCamera });
  }

  return badges;
}

/** More than `max` collapses into a "+N" whose tooltip lists the rest. */
export function BehaviourBadges({ question, max = 3 }: { question: Behaviours; max?: number }) {
  useLanguage();
  const all = behavioursOf(question);
  const visible = all.length > max ? all.slice(0, max - 1) : all;
  const hidden = all.slice(visible.length);

  if (all.length === 0) return <span className="nv-subtle">—</span>;

  return (
    <div className="nv-badge-group">
      {visible.map(({ namespace, code, tone, Icon }) => (
        <Badge
          key={`${namespace}.${code}`}
          tone={tone}
          icon={Icon}
          title={hasCode(`${namespace}Hint`, code) ? tCode(`${namespace}Hint`, code) : undefined}
        >
          {tCode(namespace, code)}
        </Badge>
      ))}
      {hidden.length > 0 ? (
        <Badge title={hidden.map((b) => tCode(b.namespace, b.code)).join(', ')}>{`+${hidden.length}`}</Badge>
      ) : null}
    </div>
  );
}
